// iter143 — Ride history detail page at /rides/:rideId.
// Tappable from the RidesHistory list card. Renders full ride detail
// (destination, full date + time, address, status, optional star rating,
// Google Maps link if coords present, Rate Ride CTA if not yet rated).
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Car, MapPin, Star, Loader2, MessageSquare, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import { useI18n } from "../lib/i18n";
import RideReviewSheet from "../components/RideReviewSheet";

function readClientId() {
  try { return localStorage.getItem("habano.client_id") || ""; } catch { return ""; }
}

function fmtFullDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return ""; }
}
function fmtTime(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(lang === "es" ? "es-ES" : "en-US", {
      hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
}

export default function RideDetail() {
  const { rideId } = useParams();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    const cid = readClientId();
    api.get(`/rides/${rideId}${cid ? `?client_id=${encodeURIComponent(cid)}` : ""}`)
      .then(({ data }) => setRide(data))
      .catch(() => setRide(null))
      .finally(() => setLoading(false));
  }, [rideId]);

  if (loading) {
    return (
      <main className="min-h-screen grid place-items-center" style={{ background: "#0B0B0B" }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "rgba(245,241,232,0.4)" }} />
      </main>
    );
  }
  if (!ride) {
    return (
      <main className="min-h-screen grid place-items-center px-6" style={{ background: "#0B0B0B" }}
        data-testid="ride-detail-not-found">
        <div className="text-center">
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#F5F1E8" }}>
            {lang === "es" ? "Viaje no encontrado" : "Ride not found"}
          </h1>
          <Link to="/rides/history" className="mt-4 inline-block"
            style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
            {lang === "es" ? "Volver al historial" : "Back to history"}
          </Link>
        </div>
      </main>
    );
  }

  const mapsUrl = (Number.isFinite(ride.lat) && Number.isFinite(ride.lng))
    ? `https://maps.google.com/?q=${ride.lat},${ride.lng}` : "";

  return (
    <main data-testid="ride-detail-page" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
      <header className="px-5 flex items-center gap-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 28px)" }}>
        <button onClick={() => navigate(-1)} aria-label="Back" data-testid="ride-detail-back"
          className="grid place-items-center" style={{ minHeight: 48, minWidth: 48, marginLeft: -8 }}>
          <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
        </button>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#F5F1E8" }}>
          {lang === "es" ? "Detalle del viaje" : "Ride details"}
        </h1>
      </header>

      <section className="mx-5 mt-6" data-testid="ride-detail-card"
        style={{ background: "#141414", border: "0.5px solid rgba(232,184,75,0.2)",
                 borderRadius: 14, padding: "20px 20px 22px" }}>
        <div className="flex items-start gap-3">
          <span className="grid place-items-center shrink-0"
            style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(232,184,75,0.12)" }}>
            <Car className="w-5 h-5" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3",
                           letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
              {lang === "es" ? "Destino" : "Destination"}
            </div>
            <div className="mt-1" data-testid="ride-detail-destination"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8", fontWeight: 600 }}>
              {ride.destination_name}
            </div>
          </div>
          <span data-testid="ride-detail-status"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#E8B84B",
                     letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700,
                     border: "1px solid rgba(232,184,75,0.4)", borderRadius: 9999,
                     padding: "5px 10px", whiteSpace: "nowrap" }}>
            {ride.status === "completed"
              ? (lang === "es" ? "Completado" : "Completed")
              : (lang === "es" ? "Solicitado" : "Requested")}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4">
          <div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3",
                           letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
              {lang === "es" ? "Fecha" : "Date"}
            </div>
            <div className="mt-1" data-testid="ride-detail-date"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8" }}>
              {fmtFullDate(ride.created_at, lang)}
            </div>
          </div>
          <div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3",
                           letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
              {lang === "es" ? "Hora" : "Time"}
            </div>
            <div className="mt-1" data-testid="ride-detail-time"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8" }}>
              {fmtTime(ride.created_at, lang)}
            </div>
          </div>
        </div>

        {ride.destination_address && (
          <div className="mt-5">
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3",
                           letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
              {lang === "es" ? "Dirección" : "Address"}
            </div>
            <div className="mt-1 inline-flex items-start gap-1.5" data-testid="ride-detail-address"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8" }}>
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#B0AAA3" }} strokeWidth={1.5} />
              <span>{ride.destination_address}</span>
            </div>
          </div>
        )}

        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="ride-detail-maps-link"
            className="mt-5 inline-flex items-center gap-2"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#E8B84B", fontWeight: 600 }}>
            <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
            {lang === "es" ? "Abrir en Google Maps" : "Open in Google Maps"}
          </a>
        )}

        <div className="mt-6 pt-5" style={{ borderTop: "1px solid rgba(232,184,75,0.15)" }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3",
                         letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
            {lang === "es" ? "Calificación" : "Rating"}
          </div>
          {ride.review_rating ? (
            <div className="mt-2 flex items-center gap-2" data-testid="ride-detail-rating">
              {[1,2,3,4,5].map((n) => (
                <Star key={n} className="w-5 h-5"
                  fill={n <= ride.review_rating ? "#E8B84B" : "none"}
                  color={n <= ride.review_rating ? "#E8B84B" : "rgba(232,184,75,0.35)"}
                  strokeWidth={1.5} />
              ))}
              <span className="ml-1" style={{ color: "#E8B84B",
                fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700 }}>
                {ride.review_rating}/5
              </span>
            </div>
          ) : (
            <button type="button" onClick={() => setReviewOpen(true)}
              data-testid="ride-detail-rate-cta"
              className="mt-3 inline-flex items-center gap-2"
              style={{ height: 48, padding: "0 18px",
                       background: "transparent", color: "#E8B84B",
                       border: "1px solid rgba(232,184,75,0.5)", borderRadius: 12,
                       fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }}>
              <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
              {lang === "es" ? "Calificar viaje" : "Rate Ride"}
            </button>
          )}
          {ride.review_comment && (
            <p className="mt-3" data-testid="ride-detail-review-comment"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8",
                       lineHeight: 1.5, fontStyle: "italic" }}>
              “{ride.review_comment}”
            </p>
          )}
        </div>
      </section>

      <RideReviewSheet
        isOpen={reviewOpen}
        rideId={rideId}
        onClose={() => setReviewOpen(false)}
        onSubmitted={(updated) => { if (updated) setRide(updated); }}
      />

      <BottomNav />
    </main>
  );
}
